import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { DEFAULTS, ICON_NAMES } from "../../data/defaults";
import { ImageUploader } from "./ImageUploader";

const SCHEMA = {
  hero: { label: "Hero", fields: ["badge", "title", "titleHighlight", "subtitle", "primaryCta", "secondaryCta"], arrays: { stats: ["value", "label"] } },
  about: { label: "About", fields: ["eyebrow", "title"], stringArrays: ["paragraphs", "points"] },
  services: { label: "Services", fields: ["eyebrow", "title", "desc"], arrays: { items: ["icon", "title", "desc"] } },
  routes: { label: "Routes", fields: ["eyebrow", "title", "desc"], arrays: { items: ["from", "to", "days"] } },
  whyUs: { label: "Why Us", fields: ["eyebrow", "title", "desc"], arrays: { items: ["icon", "title", "desc"] } },
  steps: { label: "How It Works", fields: ["eyebrow", "title"], arrays: { items: ["n", "title", "desc"] } },
  gallery: { label: "Gallery", fields: ["eyebrow", "title", "desc"], arrays: { items: ["url", "alt"] } },
  testimonials: { label: "Testimonials", fields: ["eyebrow", "title"], arrays: { items: ["name", "location", "text"] } },
  faqs: { label: "FAQ", fields: ["eyebrow", "title"], arrays: { items: ["q", "a"] } },
  contact: { label: "Contact", fields: ["eyebrow", "title", "desc", "whatsapp", "whatsapp2", "email", "location"] },
  cta: { label: "CTA Banner", fields: ["badge", "title", "desc"] },
  footer: { label: "Footer", fields: ["description", "tagline"] },
};

const TEXTAREA_FIELDS = new Set(["subtitle", "desc", "text", "a", "description"]);
const inputCls = "w-full rounded-full border border-black/10 bg-white px-4 py-2.5 text-sm text-navy outline-none transition focus:border-navy focus:ring-2 focus:ring-navy/15";
const areaCls = "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-navy outline-none transition focus:border-navy focus:ring-2 focus:ring-navy/15";
const labelCls = "mb-1.5 block text-xs font-semibold capitalize text-navy/60";

const Field = ({ name, value, onChange }) => {
  if (name === "url") {
    return <ImageUploader value={value} onChange={onChange} testid="cms-image" />;
  }
  if (name === "icon") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
    );
  }
  if (TEXTAREA_FIELDS.has(name)) {
    return <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={areaCls} />;
  }
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
};

export const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("hero");

  const fetchContent = useCallback(() => {
    return api.get("/content")
      .then(({ data }) => {
        const merged = {};
        for (const k of Object.keys(DEFAULTS)) merged[k] = { ...DEFAULTS[k], ...(data?.[k] || {}) };
        setContent(merged);
      })
      .catch(() => setContent(DEFAULTS));
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const setField = (sec, field, val) => setContent((c) => ({ ...c, [sec]: { ...c[sec], [field]: val } }));
  const setArrItem = (sec, key, i, field, val) =>
    setContent((c) => ({ ...c, [sec]: { ...c[sec], [key]: c[sec][key].map((it, idx) => idx === i ? { ...it, [field]: val } : it) } }));
  const addArrItem = (sec, key, tmpl) =>
    setContent((c) => ({ ...c, [sec]: { ...c[sec], [key]: [...(c[sec][key] || []), tmpl] } }));
  const removeArrItem = (sec, key, i) =>
    setContent((c) => ({ ...c, [sec]: { ...c[sec], [key]: c[sec][key].filter((_, idx) => idx !== i) } }));
  const setStrItem = (sec, key, i, val) =>
    setContent((c) => ({ ...c, [sec]: { ...c[sec], [key]: c[sec][key].map((v, idx) => idx === i ? val : v) } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", content);
      toast.success("Content published! Refresh the site to see it live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    await fetchContent();
    toast.info("Reverted to last saved content.");
  };

  if (!content) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  }

  const cfg = SCHEMA[active];

  return (
    <div data-testid="content-editor">
      {/* Action bar */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-white/70">Editing: <span className="font-bold text-white">{cfg.label}</span></p>
        <div className="flex gap-2">
          <button onClick={reset} data-testid="reset-content-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20">
            <RotateCcw size={15} /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="save-content-btn" className="inline-flex items-center gap-2 rounded-full bg-brandred px-5 py-2.5 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={16} />} Save &amp; Publish
          </button>
        </div>
      </div>

      {/* Section tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {Object.entries(SCHEMA).map(([sec, c]) => (
          <button
            key={sec}
            onClick={() => setActive(sec)}
            data-testid={`cms-section-${sec}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              active === sec ? "bg-white text-navy shadow-sm" : "bg-white/10 text-white/80 border border-white/15 hover:bg-white/20"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Active section card */}
      <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm sm:p-8">
        <div className="grid gap-4 sm:grid-cols-2">
          {cfg.fields.map((f) => (
            <div key={f} className={TEXTAREA_FIELDS.has(f) ? "sm:col-span-2" : ""}>
              <label className={labelCls}>{f}</label>
              <Field name={f} value={content[active][f]} onChange={(v) => setField(active, f, v)} />
            </div>
          ))}
        </div>

        {(cfg.stringArrays || []).map((key) => (
          <div key={key} className="mt-6">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wide text-navy/60">{key}</label>
              <button type="button" onClick={() => addArrItem(active, key, "")} className="inline-flex items-center gap-1 text-xs font-bold text-brandred"><Plus size={12} /> Add</button>
            </div>
            <div className="mt-2 space-y-2">
              {(content[active][key] || []).map((v, i) => (
                <div key={i} className="flex gap-2">
                  <textarea rows={2} value={v} onChange={(e) => setStrItem(active, key, i, e.target.value)} className={areaCls} />
                  <button type="button" onClick={() => removeArrItem(active, key, i)} className="shrink-0 rounded-xl border border-red-200 px-2.5 text-brandred hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}

        {Object.entries(cfg.arrays || {}).map(([key, itemFields]) => (
          <div key={key} className="mt-6">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wide text-navy/60">{key} ({(content[active][key] || []).length})</label>
              <button type="button" onClick={() => addArrItem(active, key, Object.fromEntries(itemFields.map((f) => [f, ""])))} data-testid={`cms-add-${active}-${key}`} className="inline-flex items-center gap-1 text-xs font-bold text-brandred">
                <Plus size={12} /> Add item
              </button>
            </div>
            <div className="mt-2 space-y-3">
              {(content[active][key] || []).map((it, i) => (
                <div key={i} className="rounded-2xl border border-black/5 bg-navy-50/70 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-navy/40">#{i + 1}</span>
                    <button type="button" onClick={() => removeArrItem(active, key, i)} className="rounded-lg border border-red-200 px-2 py-1 text-brandred hover:bg-red-50"><Trash2 size={13} /></button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {itemFields.map((f) => (
                      <div key={f} className={TEXTAREA_FIELDS.has(f) || f === "url" ? "sm:col-span-2" : ""}>
                        <label className={labelCls}>{f}</label>
                        <Field name={f} value={it[f]} onChange={(v) => setArrItem(active, key, i, f, v)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
